import QueryState from '../../components/query_state'
import { Combobox } from '@cinesend/ui-kit'
import AppDialog from '../../components/app_dialog'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import axios from 'axios'
import { useSnackbar } from '../../hooks/use_snackbar'
import { useParams } from 'react-router-dom'
import { MISSING_UNIQUE_NAME } from '../../constants/category_display'

const itemName = (item) => item.title ?? item.default_title ?? item.category_values?.[0]?.value ?? 'Untitled'

const itemTypeLabel = (item, ratings, showRating) => {
  if (showRating) {
    return (
      ratings?.find((rating) => String(rating.id) === String(item.field_values?.[0]?.value))?.label ?? item.plural_label
    )
  }
  return item.plural_label ?? 'Category'
}

const itemMetaLabel = (item, isCategory) => {
  if (isCategory) return item.external_unique_name || MISSING_UNIQUE_NAME
  return item.uuid ?? String(item.id)
}

const AddModal = ({ onClose, type, queryKey, category, invalidateKey, title: titleOverride }) => {
  const { addMessage } = useSnackbar()
  const [selectedIDs, setSelectedIDs] = useState([])
  const { data: items, isLoading, isError, error } = useQuery(queryKey)
  const showWarningSlateAssetFilters = type === 'warning-slates' && !queryKey?.[0].includes('&adAssignment')
  const { cycleID } = useParams()
  const { data: cycle } = useQuery([`cycles/${cycleID}`])
  const {
    data: ratingCollection,
    isInitialLoading: isRatingCollectionLoading,
    isError: isRatingCollectionError,
    error: ratingCollectionError,
  } = useQuery({
    queryKey: [`/collections?schema_id=${cycle?.schema_id}&search=Ratings`],
    enabled: showWarningSlateAssetFilters && !!cycle?.id,
  })
  const ratingCollectionId = ratingCollection?.[0]?.id
  const {
    data: ratings,
    isInitialLoading: isRatingsLoading,
    isError: isRatingError,
    error: ratingError,
  } = useQuery({
    queryKey: [`collection-values?collectionID=${ratingCollectionId}`],
    enabled: showWarningSlateAssetFilters && !!ratingCollectionId,
  })
  const isCategoryPicker = !['assets', 'warning-slates', 'ad-pools', 'interstitial'].includes(type)
  const noun = isCategoryPicker ? 'categories' : 'assets'
  const title = titleOverride ?? `Add ${noun}`
  const queryClient = useQueryClient()

  const options = useMemo(
    () =>
      (items ?? []).map((item) => {
        const name = itemName(item)
        const typeLabel = itemTypeLabel(item, ratings, showWarningSlateAssetFilters)
        const metaLabel = itemMetaLabel(item, isCategoryPicker)
        return {
          value: String(item.id),
          label: name,
          description: `${typeLabel} · ${metaLabel}`,
          keywords: `${name} ${typeLabel} ${metaLabel} ${item.id}`,
        }
      }),
    [items, ratings, showWarningSlateAssetFilters, isCategoryPicker]
  )

  const isFeaturedContent = queryKey?.[0].includes('&featuredContentAssignment')
  const addItems = useMutation(
    () => {
      return axios.post(`/categories/${category.id}/add-items`, {
        type,
        ids: selectedIDs,
        isFeaturedContent,
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(invalidateKey)
        onClose()
      },
      onError: (err) => {
        addMessage(err?.response?.data?.error ?? err.message, 'error')
      },
    }
  )

  return (
    <AppDialog
      title={title}
      onClose={onClose}
      size='md'
      confirmText='Add item'
      onConfirm={() => addItems.mutate()}
      confirmDisabled={selectedIDs.length === 0 || addItems.isLoading}
    >
      <QueryState
        isLoading={isLoading || (showWarningSlateAssetFilters && (isRatingsLoading || isRatingCollectionLoading))}
        error={
          isError
            ? error
            : showWarningSlateAssetFilters && (isRatingCollectionError || isRatingError)
              ? (ratingCollectionError ?? ratingError)
              : null
        }
      >
        <Combobox
          label={`Search ${noun}`}
          hideLabel
          className='w-full'
          multiple
          modal={false}
          placeholder='Search by name or ID...'
          emptyText={`No ${noun} found`}
          options={options}
          value={selectedIDs.map(String)}
          onValueChange={(next) => {
            const values = Array.isArray(next) ? next : next ? [next] : []
            setSelectedIDs(values.map(Number))
          }}
        />
      </QueryState>
    </AppDialog>
  )
}

export default AddModal
